import React, { useEffect, useState } from "react";
import {
    Drawer,
    Descriptions,
    Tag,
    Button,
    Space,
    message,
    Spin,
    Divider,
    Input,
    Avatar,
    List,
    Typography,
    Timeline,
    Card,
    Collapse,
    Row,
    Col,
    Form,
    Select,
    DatePicker,
    Switch,
    Modal
} from "antd";
import {
    UserOutlined,
    MessageOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CarOutlined,
    IdcardOutlined,
    PhoneOutlined,
    MailOutlined,
    CalendarOutlined,
    DollarOutlined,
    ShopOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    EyeOutlined,
    EnvironmentOutlined,
    UserSwitchOutlined,
    OrderedListOutlined,
    CommentOutlined
} from "@ant-design/icons";
import API from "../../API/API";
import moment from "moment";

const { TextArea } = Input;
const { Item } = Descriptions;
const { Text, Paragraph, Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;
const { confirm } = Modal;

// Constants from your schema
const ISSUE_STATUS = ['Open', 'In Progress', 'Waiting for Vendor', 'Resolved', 'Closed'];
const ISSUE_TYPES = [
    'Defect',
    'Missing Part',
    'Wrong Part',
    'Damage',
    'Warranty',
    'Recall',
    'Shipping Lost',
    'Cx placed incorrect order',
    'Corrosion / Rusty',
    'Miles / Tag',
    'Yard shipped incorrect part',
    'Cx provided Incorrect Spec',
    'Part does not fit',
    'Yard Issues',
    'Wiring harnesses & Plug in Switch',
    'Customer Refused the Package',
    'Other'
];
const PRIORITY_LEVELS = ['Low', 'Medium', 'High', 'Critical'];
const REPORTED_BY = ['Customer', 'Internal', 'Dealer'];

const BASE_URL = "http://localhost:5173"; // Your base URL

const getIssueTypeDisplay = (issue) => {
    if (!issue) return "N/A";
    if (issue.issueType === "Other") {
        return issue.customIssueType?.trim() || "Other";
    }
    return issue.issueType || "N/A";
};

const IssueDetailsDrawer = ({ visible, onClose, issue, onUpdate, openInEditMode = false }) => {
    const [loading, setLoading] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [issueDetails, setIssueDetails] = useState(null);
    const [loadingLead, setLoadingLead] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [leadPopupVisible, setLeadPopupVisible] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [leadDetails, setLeadDetails] = useState(null);
    const [yardDetails, setYardDetails] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [serviceAgentDetails, setServiceAgentDetails] = useState(null);
    const [loadingServiceAgent, setLoadingServiceAgent] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    
    // Yard comment states
    const [newYardComment, setNewYardComment] = useState("");
    const [submittingYardComment, setSubmittingYardComment] = useState(false);

    useEffect(() => {
        if (issue && visible) {
            fetchIssueDetails();
        }
    }, [issue, visible]);

    // The table's Edit action opens this drawer straight into edit mode, while
    // View opens it read-only. Reapplied on every open and on every change of
    // issue, because editMode otherwise persists from the previous visit — so
    // viewing an issue right after editing one would land in the edit form.
    useEffect(() => {
        if (visible) {
            setEditMode(!!openInEditMode);
        }
    }, [visible, openInEditMode, issue?._id]);

useEffect(() => {
    if (issueDetails && editMode) {
        // Get the latest yard comment if exists
        const getLatestYardComment = () => {
            if (!issueDetails.yardDescription || issueDetails.yardDescription.length === 0) {
                return "";
            }
            const sorted = [...issueDetails.yardDescription].sort((a, b) => 
                new Date(b.changedAt) - new Date(a.changedAt)
            );
            return sorted[0]?.comment || "";
        };

        // Prepare form data from issueDetails
        const formData = {
            issueType: issueDetails.issueType,
            customIssueType: issueDetails.customIssueType || "",
            priority: issueDetails.priority,
            status: issueDetails.status,
            reportedBy: issueDetails.reportedBy,
            reportedDate: issueDetails.reportedDate ? moment(issueDetails.reportedDate) : null,
            issue_created_date: moment(issueDetails.issue_created_date || issueDetails.createdAt),
            description: getIssueDescription(),
            yardDescription: getLatestYardComment(),  // Add this line
            calculationNotes: issueDetails.calculationNotes || ""
        };
        form.setFieldsValue(formData);
    }
}, [issueDetails, editMode, form]);

    const fetchIssueDetails = async () => {
        if (!issue?._id) return;

        setLoading(true);
        try {
            const res = await API.getIssueById(issue._id);
            console.log('Issue Details Response:', res);
            if (res.success) {
                setIssueDetails(res.data);
            } else {
                message.error(res.message || "Failed to fetch issue details");
            }
        } catch (err) {
            console.error(err);
            message.error("Error fetching issue details");
        } finally {
            setLoading(false);
        }
    };

    const fetchLeadDetails = async (leadId) => {
        setLoadingDetails(true);
        try {
            // Fetch lead details
            const leadRes = await API.GetLeadById(leadId);
            console.log('Full Lead API Response:', leadRes);

            if (leadRes.status && leadRes.data) {
                const leadData = leadRes.data;
                setLeadDetails(leadData);

                // Fetch Sales Agent details
                if (leadData.handledBy) {
                    const possibleUserId =
                        leadData.handledBy?.$oid ||
                        leadData.handledBy?._id ||
                        leadData.handledBy;

                    if (possibleUserId) {
                        try {
                            const userRes = await API.GetUserById(possibleUserId);
                            console.log('Sales Agent API Response:', userRes);

                            if (userRes.success && userRes.data) {
                                setUserDetails(userRes.data);
                            } else {
                                console.error('User API error:', userRes.message);
                            }
                        } catch (userErr) {
                            console.error('Error calling user API:', userErr);
                        }
                    }
                }

                // Fetch SERVICE AGENT details
                if (leadData.serviceHandledBy) {
                    setLoadingServiceAgent(true);
                    const serviceAgentId = leadData.serviceHandledBy?.$oid ||
                                           leadData.serviceHandledBy?._id ||
                                           leadData.serviceHandledBy;
                    
                    if (serviceAgentId) {
                        try {
                            const serviceRes = await API.GetUserById(serviceAgentId);
                            console.log('Service Agent API Response:', serviceRes);
                            
                            if (serviceRes.success && serviceRes.data) {
                                setServiceAgentDetails(serviceRes.data);
                            } else {
                                console.error('Service Agent API error:', serviceRes.message);
                            }
                        } catch (serviceErr) {
                            console.error('Error calling service agent API:', serviceErr);
                        } finally {
                            setLoadingServiceAgent(false);
                        }
                    }
                }

                // Check if yard array exists and has items
                if (leadData.yard && Array.isArray(leadData.yard) && leadData.yard.length > 0) {
                    const latestYard = leadData.yard[leadData.yard.length - 1];
                    console.log('Latest yard entry:', latestYard);

                    const possibleYardId = latestYard.yardId ||
                        latestYard._id?.$oid ||
                        latestYard._id;

                    if (possibleYardId) {
                        try {
                            const yardRes = await API.GetYardById(possibleYardId);
                            console.log('Yard API Response:', yardRes);

                            if (yardRes.status && yardRes.data) {
                                setYardDetails(yardRes.data);
                            } else {
                                console.error('Yard API error:', yardRes.message);
                            }
                        } catch (yardErr) {
                            console.error('Error calling yard API:', yardErr);
                        }
                    }
                }
            } else {
                message.error(leadRes.message || "Failed to fetch lead details");
            }
        } catch (err) {
            console.error('Error in fetchLeadDetails:', err);
            message.error("Error fetching lead details");
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleViewLead = (leadId) => {
        setSelectedLeadId(leadId);
        setLeadPopupVisible(true);
        setLeadDetails(null);
        setYardDetails(null);
        setUserDetails(null);
        fetchLeadDetails(leadId);
    };

    const handleCloseLeadPopup = () => {
        setLeadPopupVisible(false);
        setSelectedLeadId(null);
        setLeadDetails(null);
        setYardDetails(null);
        setUserDetails(null);
        setServiceAgentDetails(null);
        setLoadingServiceAgent(false);
    };

    const handleEdit = () => {
        setEditMode(true);
    };

    const handleCancelEdit = () => {
        form.resetFields();
        setEditMode(false);
    };

const handleSave = async () => {
    try {
        const values = await form.validateFields();
        setSaving(true);

        const updateData = {
            issueType: values.issueType,
            customIssueType: values.issueType === "Other" ? values.customIssueType?.trim() || "" : undefined,
            priority: values.priority,
            status: values.status,
            reportedBy: values.reportedBy,
            reportedDate: values.reportedDate ? values.reportedDate.toISOString() : issueDetails.reportedDate,
            issue_created_date: values.issue_created_date
                ? values.issue_created_date.toISOString()
                : undefined,
            calculationNotes: values.calculationNotes?.trim() || "",
        };

        // Handle customer description - check if changed and send as newComment
        const currentDescription = getIssueDescription();
        if (values.description && values.description !== currentDescription) {
            updateData.newComment = values.description;
        }

        // Handle yard description - check if changed and send as newYardComment
        const getLatestYardComment = () => {
            if (!issueDetails.yardDescription || issueDetails.yardDescription.length === 0) {
                return "";
            }
            const sorted = [...issueDetails.yardDescription].sort((a, b) => 
                new Date(b.changedAt) - new Date(a.changedAt)
            );
            return sorted[0]?.comment || "";
        };

        const currentYardComment = getLatestYardComment();
        if (values.yardDescription && values.yardDescription !== currentYardComment) {
            updateData.newYardComment = values.yardDescription;
        }

        // Handle status change - this will also add a comment to description
        if (values.status !== issueDetails.status) {
            const comment = await new Promise((resolve) => {
                Modal.confirm({
                    title: 'Status Change',
                    content: (
                        <div>
                            <p>You are changing status from <strong>{issueDetails.status}</strong> to <strong>{values.status}</strong>.</p>
                            <p>Please add a comment explaining the change:</p>
                            <Input.TextArea
                                rows={3}
                                placeholder="Enter comment..."
                                onChange={(e) => {
                                    window.statusChangeComment = e.target.value;
                                }}
                            />
                        </div>
                    ),
                    onOk: () => resolve(window.statusChangeComment),
                    onCancel: () => resolve(null)
                });
            });

            if (comment) {
                // If there's already a newComment being added, combine them
                if (updateData.newComment) {
                    updateData.newComment = `${updateData.newComment}\n\n[Status Change: ${issueDetails.status} → ${values.status}]\n${comment}`;
                } else {
                    updateData.newComment = `[Status Change: ${issueDetails.status} → ${values.status}]\n${comment}`;
                }
            } else {
                message.warning('Status change cancelled');
                setSaving(false);
                return;
            }
        }

        console.log('Updating issue with:', updateData);

        const res = await API.updateIssue(issueDetails._id?.$oid || issueDetails._id, updateData);

        if (res.success) {
            message.success('Issue updated successfully');
            setIssueDetails(res.data);
            if (onUpdate) onUpdate(res.data);
            setEditMode(false);
        } else {
            message.error(res.message || 'Failed to update issue');
        }
    } catch (error) {
        console.error('Validation failed:', error);
        if (error.errorFields) {
            message.error('Please fill in all required fields correctly');
        }
    } finally {
        setSaving(false);
    }
};

    const getStatusColor = (status) => {
        switch (status) {
            case 'Open': return 'red';
            case 'In Progress': return 'blue';
            case 'Waiting for Vendor': return 'orange';
            case 'Resolved': return 'green';
            case 'Closed': return 'gray';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'red';
            case 'High': return 'orange';
            case 'Medium': return 'yellow';
            case 'Low': return 'green';
            default: return 'default';
        }
    };

    const renderValue = (value) => {
        if (value === null || value === undefined) {
            return "N/A";
        }

        if (value.$date) {
            return moment(value.$date).format("MM-DD-YYYY h:mm A");
        }

        if (typeof value === 'string' && moment(value, moment.ISO_8601, true).isValid()) {
            return moment(value).format("MM-DD-YYYY h:mm A");
        }

        if (typeof value === 'object') {
            if (value.$oid) {
                return value.$oid;
            }

            if (value.personalInfo) {
                return (
                    <Space>
                        <span>{value.personalInfo.fullName || 'Unknown'}</span>
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewLead(value._id?.$oid || value._id)}
                        >
                            View Lead
                        </Button>
                    </Space>
                );
            }

            return "[Object]";
        }

        if (Array.isArray(value)) {
            if (value.length === 0) return "None";
            return `${value.length} item(s)`;
        }

        if (typeof value === 'boolean') {
            return value ? "Yes" : "No";
        }

        return value.toString();
    };

    // Handler for adding customer comment
    const handleAddComment = async () => {
        if (!newComment.trim()) {
            message.warning("Please enter a comment");
            return;
        }

        setSubmittingComment(true);
        try {
            const updateData = {
                description: [
                    ...(issueDetails.description || []),
                    {
                        status: issueDetails.status,
                        comment: newComment.trim(),
                        changedAt: new Date().toISOString()
                    }
                ]
            };

            const res = await API.updateIssue(issueDetails._id?.$oid || issueDetails._id, updateData);

            if (res.success) {
                message.success("Comment added successfully");
                setIssueDetails(res.data);
                setNewComment("");
                if (onUpdate) onUpdate(res.data);
            } else {
                message.error(res.message || "Failed to add comment");
            }
        } catch (error) {
            console.error("Error adding comment:", error);
            message.error("Error adding comment");
        } finally {
            setSubmittingComment(false);
        }
    };

    // Handler for adding yard comment
    const handleAddYardComment = async () => {
        if (!newYardComment.trim()) {
            message.warning("Please enter a yard comment");
            return;
        }

        setSubmittingYardComment(true);
        try {
            const updateData = {
                yardDescription: [
                    ...(issueDetails.yardDescription || []),
                    {
                        comment: newYardComment.trim(),
                        changedAt: new Date().toISOString()
                    }
                ]
            };

            const res = await API.updateIssue(issueDetails._id?.$oid || issueDetails._id, updateData);

            if (res.success) {
                message.success("Yard comment added successfully");
                setIssueDetails(res.data);
                setNewYardComment("");
                if (onUpdate) onUpdate(res.data);
            } else {
                message.error(res.message || "Failed to add yard comment");
            }
        } catch (error) {
            console.error("Error adding yard comment:", error);
            message.error("Error adding yard comment");
        } finally {
            setSubmittingYardComment(false);
        }
    };

    // Get activity log (customer comments)
    const getActivityLog = () => {
        if (!issueDetails?.description || !Array.isArray(issueDetails.description)) {
            return [];
        }

        return [...issueDetails.description].sort((a, b) => {
            const dateA = a.changedAt ? new Date(a.changedAt) : new Date(0);
            const dateB = b.changedAt ? new Date(b.changedAt) : new Date(0);
            return dateB - dateA;
        });
    };

    // Get yard comments log
    const getYardCommentsLog = () => {
        if (!issueDetails?.yardDescription || !Array.isArray(issueDetails.yardDescription)) {
            return [];
        }

        return [...issueDetails.yardDescription].sort((a, b) => {
            const dateA = a.changedAt ? new Date(a.changedAt) : new Date(0);
            const dateB = b.changedAt ? new Date(b.changedAt) : new Date(0);
            return dateB - dateA;
        });
    };

    const getIssueDescription = () => {
        if (!issueDetails?.description) return "No description provided.";

        if (Array.isArray(issueDetails.description)) {
            const sortedEntries = [...issueDetails.description].sort((a, b) => {
                const dateA = a.changedAt ? new Date(a.changedAt) : new Date(0);
                const dateB = b.changedAt ? new Date(b.changedAt) : new Date(0);
                return dateB - dateA;
            });
            
            const latestEntry = sortedEntries[0];
            if (latestEntry && latestEntry.comment) {
                return latestEntry.comment;
            }
            return "Description available in activity log below.";
        }

        return issueDetails.description;
    };

    const getLeadId = () => {
        if (!issueDetails?.lead) return null;

        if (typeof issueDetails.lead === 'string') {
            return issueDetails.lead;
        }

        if (issueDetails.lead.$oid) {
            return issueDetails.lead.$oid;
        }

        if (issueDetails.lead._id) {
            if (typeof issueDetails.lead._id === 'string') {
                return issueDetails.lead._id;
            }
            if (issueDetails.lead._id.$oid) {
                return issueDetails.lead._id.$oid;
            }
        }

        return null;
    };

    const getLeadUrl = () => {
        const leadId = getLeadId();
        return leadId ? `${BASE_URL}/lead-details/${leadId}` : null;
    };

    const renderLeadField = () => {
        const leadId = getLeadId();

        if (!leadId) {
            return "N/A";
        }

        if (issueDetails.lead?.personalInfo) {
            return (
                <Space>
                    <span>{issueDetails.lead.personalInfo.fullName || 'Unknown Lead'}</span>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewLead(leadId)}
                    >
                        View Details
                    </Button>
                </Space>
            );
        }

        return (
            <Space>
                <span>Lead ID: {leadId}</span>
                <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewLead(leadId)}
                >
                    View Lead
                </Button>
            </Space>
        );
    };

    const safeGet = (obj, path, defaultValue = "N/A") => {
        const keys = path.split('.');
        let result = obj;

        for (const key of keys) {
            if (result === null || result === undefined) {
                return defaultValue;
            }
            result = result[key];
        }

        return result !== undefined ? result : defaultValue;
    };

    if (!issue) return null;

    return (
        <>
            <Drawer
                title={
                    <Space>
                        <span>Issue Details</span>
                        {issueDetails && !editMode && (
                            <Tag color={getPriorityColor(issueDetails.priority)}>
                                {issueDetails.priority}
                            </Tag>
                        )}
                    </Space>
                }
                width={900}
                onClose={onClose}
                open={visible}
                bodyStyle={{ paddingBottom: 80 }}
                extra={
                    !editMode ? (
                        <Space>
                            <Button
                                icon={<EditOutlined />}
                                onClick={handleEdit}
                                type="primary"
                            >
                                Edit Issue
                            </Button>
                            <Button onClick={onClose}>
                                Close
                            </Button>
                        </Space>
                    ) : (
                        <Space>
                            <Button
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                type="primary"
                                loading={saving}
                            >
                                Save Changes
                            </Button>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={handleCancelEdit}
                            >
                                Cancel
                            </Button>
                        </Space>
                    )
                }
            >
                <Spin spinning={loading || loadingLead}>
                    {issueDetails && (
                        <>
                            {editMode ? (
                                <Form
                                    form={form}
                                    layout="vertical"
                                    size="middle"
                                >
                                    <Card title="Edit Issue Information" size="small" style={{ marginBottom: 24 }}>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="issueType"
                                                    label="Issue Type"
                                                    rules={[{ required: true, message: 'Please select issue type' }]}
                                                >
                                                    <Select
                                                        placeholder="Select issue type"
                                                        onChange={(value) => {
                                                            if (value !== "Other") {
                                                                form.setFieldsValue({ customIssueType: undefined });
                                                            }
                                                        }}
                                                    >
                                                        {ISSUE_TYPES.map(type => (
                                                            <Option key={type} value={type}>{type}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="priority"
                                                    label="Priority"
                                                    rules={[{ required: true, message: 'Please select priority' }]}
                                                >
                                                    <Select placeholder="Select priority">
                                                        {PRIORITY_LEVELS.map(priority => (
                                                            <Option key={priority} value={priority}>{priority}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) =>
                                                prevValues.issueType !== currentValues.issueType
                                            }
                                        >
                                            {({ getFieldValue }) =>
                                                getFieldValue("issueType") === "Other" && (
                                                    <Row gutter={16}>
                                                        <Col span={24}>
                                                            <Form.Item
                                                                name="customIssueType"
                                                                label="Custom Issue Type"
                                                                preserve={false}
                                                                rules={[
                                                                    { required: true, message: 'Please enter custom issue type' },
                                                                    {
                                                                        validator: (_, value) =>
                                                                            value && value.trim()
                                                                                ? Promise.resolve()
                                                                                : Promise.reject(new Error('Please enter custom issue type'))
                                                                    }
                                                                ]}
                                                            >
                                                                <Input placeholder="Enter custom issue type" />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                )
                                            }
                                        </Form.Item>

                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="status"
                                                    label="Status"
                                                    rules={[{ required: true, message: 'Please select status' }]}
                                                >
                                                    <Select placeholder="Select status">
                                                        {ISSUE_STATUS.map(status => (
                                                            <Option key={status} value={status}>{status}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="reportedBy"
                                                    label="Reported By"
                                                    rules={[{ required: true, message: 'Please select reported by' }]}
                                                >
                                                    <Select placeholder="Select reported by">
                                                        {REPORTED_BY.map(reporter => (
                                                            <Option key={reporter} value={reporter}>{reporter}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="issue_created_date"
                                                    label="Issue Created Date"
                                                    tooltip="The date the issue was raised. Every change is kept in this field's history."
                                                    rules={[{ required: true, message: 'Please select the issue created date' }]}
                                                >
                                                    <DatePicker
                                                        showTime={{ format: 'HH:mm' }}
                                                        format="YYYY-MM-DD HH:mm"
                                                        placeholder="Select issue created date"
                                                        style={{ width: '100%' }}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item
                                                    name="description"
                                                    label="Customer Description"
                                                    rules={[{ required: true, message: 'Please enter customer description' }]}
                                                >
                                                    <Input.TextArea
                                                        rows={6}
                                                        placeholder="Enter customer description..."
                                                        showCount
                                                        maxLength={2000}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={16}>
    <Col span={24}>
        <Form.Item
            name="yardDescription"
            label="Yard Description"
            tooltip="Internal notes about yard communication"
        >
            <Input.TextArea
                rows={4}
                placeholder="Enter yard comments..."
                showCount
                maxLength={1000}
            />
        </Form.Item>
    </Col>
</Row>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item
                                                    name="calculationNotes"
                                                    label="Calculation Notes (Optional)"
                                                    tooltip="Any calculation-related notes for this issue"
                                                >
                                                    <Input.TextArea
                                                        rows={3}
                                                        placeholder="Add any calculation notes here..."
                                                        showCount
                                                        maxLength={1000}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Form>
                            ) : (
                                <>
                                    {/* Issue Header */}
                                    <div style={{ marginBottom: 24 }}>
                                        <Title level={4} style={{ marginBottom: 8 }}>
                                            {getIssueTypeDisplay(issueDetails)}
                                            <Tag
                                                color={getPriorityColor(issueDetails.priority)}
                                                style={{ marginLeft: 12 }}
                                            >
                                                {issueDetails.priority || "N/A"}
                                            </Tag>
                                            <Tag
                                                color={getStatusColor(issueDetails.status)}
                                                style={{ marginLeft: 8 }}
                                            >
                                                {issueDetails.status || "N/A"}
                                            </Tag>
                                        </Title>
                                        <Text>{renderLeadField()}</Text>
                                    </div>

                                    {/* All Fields Display */}
                                    <Card title="Issue Information" size="small" style={{ marginBottom: 24 }}>
                                        <Descriptions bordered column={1} size="small">
                                            <Item label="Issue Type">{getIssueTypeDisplay(issueDetails)}</Item>
                                            <Item label="Priority">
                                                <Tag color={getPriorityColor(issueDetails.priority)}>
                                                    {issueDetails.priority || "N/A"}
                                                </Tag>
                                            </Item>
                                            <Item label="Status">
                                                <Tag color={getStatusColor(issueDetails.status)}>
                                                    {issueDetails.status || "N/A"}
                                                </Tag>
                                            </Item>
                                            <Item label="Reported By">{issueDetails.reportedBy || "N/A"}</Item>
                                            <Item label="Reported Date">
                                                {renderValue(issueDetails.reportedDate)}
                                            </Item>
                                            <Item label="Lead">
                                                {renderLeadField()}
                                            </Item>
                                            <Item label="Calculation Notes">
                                                {issueDetails.calculationNotes?.trim() ? (
                                                    <span style={{ whiteSpace: "pre-wrap" }}>{issueDetails.calculationNotes}</span>
                                                ) : (
                                                    <Text type="secondary">No notes</Text>
                                                )}
                                            </Item>
                                            <Item label="Created By">{issueDetails.createdBy || "N/A"}</Item>
                                            <Item label="Created At">
                                                {renderValue(issueDetails.createdAt)}
                                            </Item>
                                            <Item label="Updated At">
                                                {renderValue(issueDetails.updatedAt)}
                                            </Item>
                                            <Item label="Deleted">
                                                {issueDetails.isDeleted ? "Yes" : "No"}
                                            </Item>
                                            <Item label="Version">
                                                {issueDetails.__v || "0"}
                                            </Item>
                                        </Descriptions>
                                    </Card>

                                    {/* ========== CUSTOMER COMMENTS SECTION ========== */}
                                    <Divider orientation="left">
                                        <CommentOutlined /> Customer Comments ({getActivityLog().length})
                                    </Divider>

                                    {/* Add Customer Comment */}
                                  

                                    {/* Customer Comments Timeline */}
                                    {getActivityLog().length > 0 ? (
                                        <Timeline mode="left">
                                            {getActivityLog().map((activity, index) => (
                                                <Timeline.Item
                                                    key={activity._id?.$oid || index}
                                                    color={getStatusColor(activity.status)}
                                                    dot={index === 0 ? <MessageOutlined /> : <ClockCircleOutlined />}
                                                >
                                                    <Card size="small" style={{ marginBottom: 8 }}>
                                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                            <div>
                                                                <Tag color={getStatusColor(activity.status)}>
                                                                    {activity.status || "No Status"}
                                                                </Tag>
                                                                {index === 0 && (
                                                                    <Tag color="blue">Latest</Tag>
                                                                )}
                                                            </div>
                                                            <Paragraph>
                                                                {activity.comment}
                                                            </Paragraph>
                                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                <CalendarOutlined /> {renderValue(activity.changedAt)}
                                                            </Text>
                                                        </Space>
                                                    </Card>
                                                </Timeline.Item>
                                            ))}
                                        </Timeline>
                                    ) : (
                                        <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                                            No customer comments yet.
                                        </Text>
                                    )}

                                    {/* ========== YARD COMMENTS SECTION ========== */}
                                    <Divider orientation="left">
                                        <ShopOutlined style={{ color: '#fa8c16' }} /> Yard Comments ({getYardCommentsLog().length})
                                    </Divider>

                                   

                                    {/* Yard Comments Timeline */}
                                    {getYardCommentsLog().length > 0 ? (
                                        <Timeline mode="left">
                                            {getYardCommentsLog().map((comment, index) => (
                                                <Timeline.Item
                                                    key={comment._id?.$oid || index}
                                                    color="orange"
                                                    dot={index === 0 ? <ShopOutlined /> : <ClockCircleOutlined />}
                                                >
                                                    <Card size="small" style={{ marginBottom: 8, borderLeft: '3px solid #fa8c16' }}>
                                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                            <div>
                                                                {index === 0 && (
                                                                    <Tag color="orange">Latest</Tag>
                                                                )}
                                                            </div>
                                                            <Paragraph>
                                                                {comment.comment}
                                                            </Paragraph>
                                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                                <CalendarOutlined /> {renderValue(comment.changedAt)}
                                                            </Text>
                                                        </Space>
                                                    </Card>
                                                </Timeline.Item>
                                            ))}
                                        </Timeline>
                                    ) : (
                                        <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                                            No yard comments yet.
                                        </Text>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Spin>
            </Drawer>

            {/* Lead Details Popup Modal */}
            <Modal
                title={
                    <Space>
                        <UserOutlined />
                        <span>Lead Details</span>
                        {leadDetails && (
                            <Tag color="blue">
                                {safeGet(leadDetails, 'orderNumber')}
                            </Tag>
                        )}
                    </Space>
                }
                open={leadPopupVisible}
                onCancel={handleCloseLeadPopup}
                footer={[
                    <Button key="close" onClick={handleCloseLeadPopup}>
                        Close
                    </Button>,
                    getLeadUrl() && (
                        <Button
                            key="open"
                            type="primary"
                            onClick={() => window.open(getLeadUrl(), '_blank')}
                            icon={<EyeOutlined />}
                        >
                            Open in Full View
                        </Button>
                    )
                ]}
                width={800}
                centered
            >
                <Spin spinning={loadingDetails}>
                    {leadDetails ? (
                        <div style={{ padding: '8px 0' }}>
                            {/* Personal Information */}
                            <Card
                                title={
                                    <Space>
                                        <UserOutlined />
                                        <span>Personal Information</span>
                                    </Space>
                                }
                                size="small"
                                style={{ marginBottom: 16 }}
                            >
                                <Descriptions bordered column={2} size="small">
                                    <Item label={<Space><UserOutlined /> Full Name</Space>}>
                                        {safeGet(leadDetails, 'personalInfo.fullName')}
                                    </Item>
                                    <Item label={<Space><PhoneOutlined /> Phone</Space>}>
                                        {safeGet(leadDetails, 'personalInfo.phoneNumber')}
                                    </Item>
                                    <Item label={<Space><MailOutlined /> Email</Space>}>
                                        {safeGet(leadDetails, 'personalInfo.email')}
                                    </Item>
                                    <Item label={<Space><EnvironmentOutlined /> Zip Code</Space>}>
                                        {safeGet(leadDetails, 'personalInfo.zipCode')}
                                    </Item>
                                    <Item label="Domain" span={2}>
                                        {safeGet(leadDetails, 'domain')}
                                    </Item>
                                </Descriptions>
                            </Card>

                            {/* Vehicle Information */}
                            <Card
                                title={
                                    <Space>
                                        <CarOutlined />
                                        <span>Vehicle Information</span>
                                    </Space>
                                }
                                size="small"
                                style={{ marginBottom: 16 }}
                            >
                                <Descriptions bordered column={2} size="small">
                                    <Item label="Make">
                                        {safeGet(leadDetails, 'vehicleInfo.make')}
                                    </Item>
                                    <Item label="Model">
                                        {safeGet(leadDetails, 'vehicleInfo.model')}
                                    </Item>
                                    <Item label="Year">
                                        {safeGet(leadDetails, 'vehicleInfo.year')}
                                    </Item>
                                    <Item label="Part">
                                        {safeGet(leadDetails, 'vehicleInfo.part')}
                                    </Item>
                                    <Item label="VIN Number" span={2}>
                                        {safeGet(leadDetails, 'vehicleInfo.vin')}
                                    </Item>
                                </Descriptions>
                            </Card>

                            {/* Order Information */}
                            <Card
                                title={
                                    <Space>
                                        <OrderedListOutlined />
                                        <span>Order Information</span>
                                    </Space>
                                }
                                size="small"
                                style={{ marginBottom: 16 }}
                            >
                                <Descriptions bordered column={2} size="small">
                                    <Item label="Order Number">
                                        {safeGet(leadDetails, 'orderNumber') || 'N/A'}
                                    </Item>
                                    <Item label={<Space><CalendarOutlined /> Order Date</Space>}>
                                        {leadDetails.orderDate ?
                                            moment(leadDetails.orderDate.$date || leadDetails.orderDate).format("MM-DD-YYYY h:mm A")
                                            : "N/A"}
                                    </Item>
                                </Descriptions>
                            </Card>

                            {/* SALES AGENT CARD */}
                            <Card
                                title={
                                    <Space>
                                        <UserSwitchOutlined style={{ color: '#722ed1' }} />
                                        <span>Sales Agent</span>
                                    </Space>
                                }
                                size="small"
                                style={{ marginBottom: 16, borderLeft: '4px solid #722ed1' }}
                            >
                                {loadingDetails ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <Spin size="small" />
                                        <Text type="secondary" style={{ marginLeft: 8 }}>Loading sales agent...</Text>
                                    </div>
                                ) : userDetails ? (
                                    <Descriptions bordered column={2} size="small">
                                        <Item label="Name" span={2}>
                                            <Space>
                                                <Avatar size="small" style={{ backgroundColor: '#722ed1' }}>
                                                    {userDetails.firstName?.[0] || userDetails.lastName?.[0] || 'A'}
                                                </Avatar>
                                                <Text strong>
                                                    {`${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 
                                                     userDetails.userName || 'N/A'}
                                                </Text>
                                            </Space>
                                        </Item>
                                        <Item label="Email">
                                            {userDetails.email ? (
                                                <a href={`mailto:${userDetails.email}`}>{userDetails.email}</a>
                                            ) : 'N/A'}
                                        </Item>
                                        <Item label="Phone">
                                            {userDetails.directNumber || 'N/A'}
                                        </Item>
                                        <Item label="Role">
                                            <Tag color="purple">{userDetails.role || 'SALES-AGENT'}</Tag>
                                        </Item>
                                        <Item label="Status" span={2}>
                                            <Tag color={userDetails.status === 'active' ? 'green' : 'default'}>
                                                {userDetails.status || 'Active'}
                                            </Tag>
                                        </Item>
                                    </Descriptions>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                        <UserSwitchOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                                        <div>No sales agent assigned to this lead</div>
                                    </div>
                                )}
                            </Card>

                            {/* SERVICE AGENT CARD */}
                            <Card
                                title={
                                    <Space>
                                        <TeamOutlined style={{ color: '#52c41a' }} />
                                        <span>Service Agent</span>
                                    </Space>
                                }
                                size="small"
                                style={{ marginBottom: 16, borderLeft: '4px solid #52c41a' }}
                            >
                                {loadingServiceAgent ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <Spin size="small" />
                                        <Text type="secondary" style={{ marginLeft: 8 }}>Loading service agent...</Text>
                                    </div>
                                ) : serviceAgentDetails ? (
                                    <Descriptions bordered column={2} size="small">
                                        <Item label="Name" span={2}>
                                            <Space>
                                                <Avatar size="small" style={{ backgroundColor: '#52c41a' }}>
                                                    {serviceAgentDetails.firstName?.[0] || serviceAgentDetails.lastName?.[0] || 'S'}
                                                </Avatar>
                                                <Text strong>
                                                    {`${serviceAgentDetails.firstName || ''} ${serviceAgentDetails.lastName || ''}`.trim() || 
                                                     serviceAgentDetails.userName || 'N/A'}
                                                </Text>
                                            </Space>
                                        </Item>
                                        <Item label="Email">
                                            {serviceAgentDetails.email ? (
                                                <a href={`mailto:${serviceAgentDetails.email}`}>{serviceAgentDetails.email}</a>
                                            ) : 'N/A'}
                                        </Item>
                                        <Item label="Phone">
                                            {serviceAgentDetails.directNumber || 'N/A'}
                                        </Item>
                                        <Item label="Role">
                                            <Tag color="green">{serviceAgentDetails.role || 'SERVICE-AGENT'}</Tag>
                                        </Item>
                                        <Item label="Status" span={2}>
                                            <Tag color={serviceAgentDetails.status === 'active' ? 'green' : 'default'}>
                                                {serviceAgentDetails.status || 'Active'}
                                            </Tag>
                                        </Item>
                                    </Descriptions>
                                ) : leadDetails?.serviceHandledBy ? (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#faad14' }}>
                                        <TeamOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                                        <div>Service agent assigned but details failed to load</div>
                                        <Button 
                                            size="small" 
                                            type="link" 
                                            onClick={() => fetchLeadDetails(selectedLeadId)}
                                            style={{ marginTop: 8 }}
                                        >
                                            Retry Loading
                                        </Button>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                        <TeamOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                                        <div>No service agent assigned to this lead</div>
                                    </div>
                                )}
                            </Card>

                            {/* Yard Information */}
                            {yardDetails && (
                                <Card
                                    title={
                                        <Space>
                                            <ShopOutlined />
                                            <span>Yard Information</span>
                                        </Space>
                                    }
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                >
                                    <Descriptions bordered column={2} size="small">
                                        <Item label="Yard Name">
                                            {safeGet(yardDetails, 'yardName')}
                                        </Item>
                                        <Item label="Contact Person">
                                            {safeGet(yardDetails, 'contactPerson')}
                                        </Item>
                                        <Item label="Email">
                                            {safeGet(yardDetails, 'email')}
                                        </Item>
                                        <Item label="Contact Numbers">
                                            {yardDetails.contactNumbers ?
                                                (Array.isArray(yardDetails.contactNumbers) ?
                                                    yardDetails.contactNumbers.join(", ")
                                                    : String(yardDetails.contactNumbers))
                                                : "N/A"}
                                        </Item>
                                        <Item label="Website">
                                            {yardDetails.link ? (
                                                <a href={yardDetails.link} target="_blank" rel="noopener noreferrer">
                                                    {yardDetails.link}
                                                </a>
                                            ) : "N/A"}
                                        </Item>
                                        {yardDetails.yardAddress && (
                                            <Item label="Address" span={2}>
                                                {[
                                                    yardDetails.yardAddress.address,
                                                    yardDetails.yardAddress.street,
                                                    yardDetails.yardAddress.city,
                                                    yardDetails.yardAddress.state,
                                                    yardDetails.yardAddress.country,
                                                    yardDetails.yardAddress.zip_code
                                                ].filter(Boolean).join(", ")}
                                            </Item>
                                        )}
                                    </Descriptions>
                                </Card>
                            )}
                        </div>
                    ) : selectedLeadId ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <Spin size="large" />
                            <Paragraph style={{ marginTop: 16 }}>Loading lead details...</Paragraph>
                        </div>
                    ) : null}
                </Spin>
            </Modal>
        </>
    );
};

export default IssueDetailsDrawer;
